/**
 * The public-hazard level a priority makes — the twin of
 * `App\Support\Maintenance\PublicHazards::LEVELS`.
 *
 * A maintenance item flagged as a public hazard does not carry a level
 * of its own: the priority step already weighs whether somebody could
 * get hurt and whether people can get past, so the level is READ off
 * the priority. The four words are the ones trail signage uses.
 *
 * Used where the level is worked out on the device (the wizard and the
 * edit panels, which preview what the public will see) and where a
 * public page colours a level the server already named.
 * `harness/publichazards_verify.php` runs this file against the PHP.
 */
import { parseCalendarDate } from './calendarDate.js';
import { isOutstanding } from './maintenanceStatus.js';

export const PUBLIC_HAZARD_LEVELS = {
    critical: { key: 'danger', label: 'Danger' },
    high: { key: 'warning', label: 'Warning' },
    medium: { key: 'caution', label: 'Caution' },
    low: { key: 'notice', label: 'Notice' },
};

/** Sign colours, most severe first: red, orange, yellow, blue. */
export const PUBLIC_HAZARD_TONES = {
    danger: { bg: '#b42318', fg: '#ffffff', soft: '#fde8e6', ink: '#912018' },
    warning: { bg: '#e8590c', fg: '#ffffff', soft: '#fff0e5', ink: '#9a3a06' },
    caution: { bg: '#f5c518', fg: '#1a1a1a', soft: '#fdf6d8', ink: '#6b5200' },
    notice: { bg: '#1d5fb4', fg: '#ffffff', soft: '#e6eefa', ink: '#164a8c' },
};

/**
 * A structure's condition → level, for one an inspection published —
 * the twin of `PublicHazards::STRUCTURE_LEVELS`. Fair and good are
 * absent: they are not a warning, and the server takes the flag off a
 * structure rated either.
 */
export const STRUCTURE_HAZARD_LEVELS = {
    failed: 'danger',
    poor: 'warning',
    marginal: 'caution',
};

export function publicHazardLevel(priority) {
    return PUBLIC_HAZARD_LEVELS[priority] || PUBLIC_HAZARD_LEVELS.medium;
}

/** Safety risk + passable → the priority whose level the public sees.
 *  Twin of PublicHazards::RISK_PRIORITIES. */
export const RISK_PRIORITIES = {
    serious_risk: { blocked: 'critical', open: 'high' },
    minor_risk: { blocked: 'high', open: 'medium' },
};

/** The level a task shows the public: its safety risk and passable
 *  answers first, the priority only when no risk was answered. Twin of
 *  PublicHazards::issueLevel. A form's select may hand back a string. */
export function issueHazardLevel(item) {
    const row = RISK_PRIORITIES[item?.safety_risk];
    if (row) {
        const p = item.is_passable;
        const blocked = p === false || p === 'false' || p === 0 || p === '0';
        return publicHazardLevel(row[blocked ? 'blocked' : 'open']);
    }
    return publicHazardLevel(item?.priority);
}

/** Whether a task may warn the public: on a trail, and not judged No risk
 *  (the server refuses that pair too — MaintenanceIssue's saving hook). */
export function issueHazardOffered(item) {
    return !!(item && item.trail_id && item.safety_risk !== 'no_risk');
}

export function publicHazardTone(levelKey) {
    return PUBLIC_HAZARD_TONES[levelKey] || PUBLIC_HAZARD_TONES.caution;
}

/** The level a structure's condition makes, or null when it is not a warning. */
export function structureHazardLevel(condition) {
    const key = STRUCTURE_HAZARD_LEVELS[condition];
    if (!key) return null;
    return Object.values(PUBLIC_HAZARD_LEVELS).find((l) => l.key === key) || null;
}

/** "Reported Oct 3" from a calendar date (YYYY-MM-DD), read as a DAY. */
export function hazardReportedLabel(ymd, verb = 'Reported') {
    const d = parseCalendarDate(ymd);
    if (!d) return '';
    return verb + ' ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * The badge a crew list shows on an item the PUBLIC can see right now,
 * or null. Asks exactly what `PublicHazards::byTrail` asks — flagged,
 * on a trail, outstanding — so the badge is on screen precisely while
 * a public page is showing the item: switched on but resolved, or
 * still waiting for review, carries no badge, because nothing public
 * is showing it. Coloured by the level the public sees.
 */
export function publicHazardBadge(issue) {
    if (!issue || !issue.public_hazard) return null;
    if (!(issue.trail_id || issue.trail?.id)) return null;
    if (!isOutstanding(issue.status)) return null;
    const level = issueHazardLevel(issue);
    const tone = publicHazardTone(level.key);
    return {
        level: level.key,
        label: 'Public hazard',
        title: `Shown to the public as ${level.label} until it is resolved`,
        style: { background: tone.bg, color: tone.fg },
    };
}

/**
 * The day line for a public hazard row: "Reported Oct 3" for an item,
 * "Inspected Oct 3" for a structure an inspection published — the
 * server names which (`reported_verb`).
 */
export function hazardWhen(h) {
    return hazardReportedLabel(h?.reported_on, h?.reported_verb || 'Reported');
}

/**
 * What a public row says under its title, in order: what KIND of
 * hazard it is ("Bridge on the trail" — a structure, not the trail
 * itself), what the newest inspection found, whether people can get
 * past, and the day. Each field is the server's; any may be absent.
 * One definition so every public list says the same thing. A list that
 * prints the day in a column of its own passes `withWhen: false` and
 * puts `hazardWhen(h)` there.
 */
export function hazardDetails(h, { withWhen = true } = {}) {
    return [h?.subject, h?.detail, h?.passable_label, withWhen ? hazardWhen(h) : '']
        .map((x) => String(x || '').trim())
        .filter(Boolean);
}

/**
 * The same badge for a STRUCTURE, or null — on while a public page is
 * showing it: flagged, on a trail, and rated marginal or worse.
 */
export function structureHazardBadge(structure) {
    if (!structure || !structure.public_hazard || !structure.trail_id) return null;
    const level = structureHazardLevel(structure.condition);
    if (!level) return null;
    const tone = publicHazardTone(level.key);
    return {
        level: level.key,
        label: 'Public hazard',
        title: `Shown to the public as ${level.label} until an inspection rates it fair or good`,
        style: { background: tone.bg, color: tone.fg },
    };
}
